import { Anchor, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import {
  StaticDatasetDependency,
  StaticDatasetUsage,
  StaticDatasetUsageReport,
} from '@/types/datasets.ts';

const groups: Record<StaticDatasetDependency['kind'], { title: string; action: string }> = {
  saved_draft: {
    title: 'Saved draft',
    action: 'Remove or change these inputs, including saved subflows, then save the draft.',
  },
  published_automation: {
    title: 'Published automations',
    action: 'Update and publish the affected automations before removing this source.',
  },
  run: {
    title: 'Unfinished runs',
    action: 'This source remains required while these runs are unfinished.',
  },
  test_batch: {
    title: 'Test batches',
    action: 'Queued, active, or resumable tests still require this source.',
  },
  release_candidate: {
    title: 'Saved release candidates',
    action: 'Review these candidates and their source selections before removing this source.',
  },
};

function statusLabel(dependency: StaticDatasetDependency) {
  if (dependency.kind === 'test_batch' && dependency.status === 'failed')
    return 'failed · can be resumed';
  const labels: Record<string, string> = {
    pending: 'queued',
    preprocessing: 'preparing inputs',
    active: 'running',
    active_with_errors: 'running with errors',
    ask_user: 'waiting for a response',
    rerun_preparing: 'preparing retry',
    rerunning: 'retrying',
    frozen: 'saved for release',
  };
  return dependency.status
    ? (labels[dependency.status] ?? dependency.status.replaceAll('_', ' '))
    : null;
}

function destination(dependency: StaticDatasetDependency, allowDraftNavigation: boolean) {
  if (dependency.visibility !== 'readable' || !dependency.automation_id) return undefined;
  const base = `/automations/${encodeURIComponent(dependency.automation_id)}`;
  if (dependency.kind === 'run' && dependency.consumer_id)
    return `${base}/runs/${encodeURIComponent(dependency.consumer_id)}`;
  if (dependency.kind === 'release_candidate' && dependency.consumer_id)
    return `${base}/release?release=${encodeURIComponent(dependency.consumer_id)}`;
  if (dependency.kind === 'published_automation') return base;
  if (dependency.kind === 'saved_draft' && allowDraftNavigation) {
    const params = new URLSearchParams();
    if (dependency.subflow_id) {
      params.set('scope', 'local_subflow');
      params.set('subflow', dependency.subflow_id);
    }
    if (dependency.step_id) params.set('step', dependency.step_id);
    return `${base}/editor${params.size ? `?${params}` : ''}`;
  }
  return undefined;
}

export default function StaticDatasetUsageDetails({
  report,
  item,
  loading = false,
  failed = false,
  paused = false,
  onRefresh,
  removal = false,
  allowDraftNavigation = false,
}: {
  report?: StaticDatasetUsageReport;
  item?: StaticDatasetUsage;
  loading?: boolean;
  failed?: boolean;
  paused?: boolean;
  onRefresh?: () => void;
  removal?: boolean;
  allowDraftNavigation?: boolean;
}) {
  const hasObservation = !!report && !!item;
  const outdated = failed || paused;
  const detailsAvailable = hasObservation && report.dependencies_included === true;
  const complete = hasObservation && report.active_complete;
  const knownUses = !!item?.uses.length;
  const lastCheckOnly = loading || outdated;
  return (
    <Stack gap="xs" style={{ overflowWrap: 'anywhere' }}>
      <Text size="sm" c="dimmed">
        These are saved references, not a record of when the file was read.
      </Text>
      {loading && (
        <Text size="sm" role="status">
          {hasObservation ? 'Refreshing saved references…' : 'Checking saved references…'}
        </Text>
      )}
      {outdated && (
        <Text size="sm" role="status">
          {paused ? 'Waiting for a connection.' : 'Dependencies could not be refreshed.'}{' '}
          {hasObservation
            ? 'The details below are from the last check.'
            : 'Dependency details are unavailable.'}{' '}
          Removal is unavailable until a fresh check succeeds.
        </Text>
      )}
      {hasObservation && (
        <>
          <Text size="sm" fw={500}>
            {knownUses
              ? lastCheckOnly
                ? 'References to this source were found at the last check.'
                : 'This source has current references.'
              : complete
                ? lastCheckOnly
                  ? 'No saved references were found at the last check.'
                  : 'No current saved references found.'
                : "We couldn't confirm whether this source is still needed."}
          </Text>
          {!complete && (
            <Text size="sm">Some dependencies could not be checked. Removal is unavailable.</Text>
          )}
          {report.inspection_issues?.map((issue) => (
            <Text size="sm" key={issue}>
              {issue}
            </Text>
          ))}
          {!detailsAvailable && <Text size="sm">Dependency details are unavailable.</Text>}
          {detailsAvailable &&
            Object.entries(groups).map(([kind, group]) => {
              const dependencies =
                item.dependencies?.filter((dependency) => dependency.kind === kind) ?? [];
              if (!dependencies.length) return null;
              return (
                <Stack gap={4} key={kind}>
                  <Text size="sm" fw={600}>
                    {group.title}
                  </Text>
                  {kind === 'saved_draft' && (
                    <Text size="xs" c="dimmed">
                      Saved selections count even when an input field is disabled.
                    </Text>
                  )}
                  {dependencies.map((dependency, index) => {
                    const label = dependency.step_name
                      ? `${dependency.step_name}${dependency.subflow_name ? ` · ${dependency.subflow_name}` : ''}`
                      : dependency.label;
                    const href = destination(
                      dependency,
                      allowDraftNavigation &&
                        complete &&
                        !lastCheckOnly &&
                        dependency.version_id === report.latest_version_id,
                    );
                    return (
                      <Text
                        size="sm"
                        key={`${dependency.consumer_id}-${dependency.step_id}-${index}`}
                      >
                        {href ? (
                          <Anchor
                            component={Link}
                            size="sm"
                            c="inherit"
                            to={href}
                            target={dependency.kind === 'saved_draft' ? undefined : '_blank'}
                            rel={
                              dependency.kind === 'saved_draft' ? undefined : 'noopener noreferrer'
                            }
                            title={
                              dependency.kind === 'saved_draft' ? undefined : 'Opens in a new tab'
                            }
                          >
                            {label}
                          </Anchor>
                        ) : (
                          label
                        )}
                        {dependency.status ? ` · ${statusLabel(dependency)}` : ''}
                        {dependency.scope === 'local_subflow' &&
                          ' · saved subflow (including uncalled definitions)'}
                      </Text>
                    );
                  })}
                  {removal && (
                    <Text size="sm" c="dimmed">
                      {group.action}
                    </Text>
                  )}
                </Stack>
              );
            })}
          {item.dependency_details_truncated && (
            <Text size="sm">
              Additional dependencies are not shown because the detail limit was reached.
            </Text>
          )}
          <Text size="xs" c="dimmed">
            Last checked {new Date(report.observed_at).toLocaleString()}
          </Text>
        </>
      )}
      {!hasObservation && !loading && !outdated && (
        <Text size="sm">
          Dependency details are unavailable. Check again before removing this source.
        </Text>
      )}
      {onRefresh && (
        <StyledButton
          styles={{
            root: {
              backgroundColor: 'var(--mantine-color-gray-0)',
              color: 'var(--mantine-color-gray-7)',
              alignSelf: 'flex-start',
            },
          }}
          variant="subtle"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
        >
          {failed || paused ? 'Retry dependency check' : 'Refresh dependencies'}
        </StyledButton>
      )}
    </Stack>
  );
}
